/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { ClubMembershipPageContent, ImageTransformation, PromoPointContent, TRANSFORMATIONS } from '@services';
import classnames from 'classnames/bind';
import styles from './ClubMembershipStaticContent.module.scss';
import { Typography } from 'gd-design-library';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ClubMembershipStaticContentSkeleton } from './ClubMembershipStaticContent.skeleton';
import { ALL_IMAGES_TRANSFORMATIONS } from '@utils';

const cx = classnames.bind(styles);

const CN = 'club-membership-static-content';
const topBannerContainerClassName = `${CN}__top-banner-container`;
const topBannerImageClassName = `${topBannerContainerClassName}__image`;
const topBannerTextClassName = `${topBannerContainerClassName}__text`;
const titleContainerClassName = `${CN}__title-container`;
const promoPointsContainerClassName = `${CN}__promo-points-container`;
const promoPointClassName = `${promoPointsContainerClassName}__promo-point`;
const rightAlignedImageClassName = 'right-aligned-image';
const promoTextContainerClassName = 'promo-point-text';
const promoImageClassName = 'promo-point-image';
const faqContainerClassName = `${CN}__faq-container`;
const faqTextClassName = `${faqContainerClassName}__text`;

interface Props {
  content: ClubMembershipPageContent | null
}

export const ClubMembershipStaticContent: React.FC<Props> = ({
  content
}) => {
  if (!content) {
    return <ClubMembershipStaticContentSkeleton />;
  }

  const {
    top_banner,
    headline,
    promo_points,
    membership_faq
  } = content;

  const imageTransformation = new ImageTransformation();

  const renderPromoPoint = (promo: PromoPointContent): React.ReactElement => {
    const imageUrl = imageTransformation.transformById(
      promo.cloudinary_image?.[0]?.public_id,
      [ ...ALL_IMAGES_TRANSFORMATIONS, { name: TRANSFORMATIONS.RADIUS, params: { radius: 50 } } ],
      promo.image.url
    );

    return (
      <div className={ cx(promoPointClassName, { [rightAlignedImageClassName]: promo.right_aligned_image }) }>
        <img className={ cx(promoImageClassName) } src={ imageUrl } />
        <div className={ cx(promoTextContainerClassName) }>
          <Typography variant='h6'>{ promo.promo_text[0].title_text }</Typography>
          <Typography variant='caption'>{ promo.promo_text[0].description_text }</Typography>
        </div>
      </div>
    );
  };

  const topBannerMobileUrl = imageTransformation.transformById(
    top_banner[0]?.cloudinary_image_mobile?.[0]?.public_id,
    ALL_IMAGES_TRANSFORMATIONS,
    top_banner[0].image_mobile.url
  );

  const topBannerUrl = imageTransformation.transformById(
    top_banner[0].cloudinary_image?.[0]?.public_id,
    ALL_IMAGES_TRANSFORMATIONS,
    top_banner[0].image.url
  );

  return (
    <div className={ cx(CN) }>
      <div className={ cx(topBannerContainerClassName) }>
        <picture>
          <source srcSet={ topBannerMobileUrl } media='(max-width: 768px)'/>
          <source srcSet={ topBannerUrl }/>
          <img src={ topBannerUrl } className={ cx(topBannerImageClassName) }/>
        </picture>
        <div className={ cx(topBannerTextClassName) }>
          <Typography variant='h4'>{ top_banner[0].text[0].title_text }</Typography>
          <Typography>{ top_banner[0].text[0].description_text }</Typography>
        </div>
      </div>
      <div className={ cx(titleContainerClassName) }>
        <Typography variant='h4'>{ headline[0].title_text }</Typography>
        <Typography variant='caption'>{ headline[0].description_text }</Typography>
      </div>
      <div className={ cx(promoPointsContainerClassName) }>
        {promo_points.map(renderPromoPoint)}
      </div>
      <div className={ cx(faqContainerClassName) }>
        <Typography className={ cx(faqTextClassName) } variant='h6'>{membership_faq[0].headline}</Typography>
        {membership_faq[0].accordion_items.map((accordionItem, index) => (
          <Accordion
            key={ accordionItem.answer_text + index }
            disableGutters
            elevation={ 0 }
          >
            <AccordionSummary
              expandIcon={ <KeyboardArrowDownIcon color='inherit' /> }
            >
              <Typography variant='h5'>{accordionItem.question_text}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {accordionItem.answer_text}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  );
};
